/**
 * Mock project data for the Engineering Platform.
 * Replace with real API calls once the backend Projects API is implemented.
 *
 * Project schema:
 *   id          string   — unique identifier
 *   name        string   — project display name
 *   client      string   — client / organisation name
 *   status      "active" | "completed" | "on-hold" | "draft"
 *   type        string   — engineering discipline / project type
 *   location    string   — city, state
 *   startDate   string   — ISO date string (YYYY-MM-DD)
 *   updatedAt   string   — ISO date string
 *   drawingsCount number — number of drawings in this project
 *   description string   — short project description
 */

export const mockProjects = [
  {
    id: "proj_001",
    name: "Westfield Sydney Tower Retrofit",
    client: "Scentre Group",
    status: "active",
    type: "Commercial",
    location: "Sydney, NSW",
    startDate: "2024-09-01",
    updatedAt: "2025-02-12",
    drawingsCount: 47,
    description: "Mechanical and fire safety upgrade to existing commercial tower structure per NCC 2022.",
  },
  {
    id: "proj_002",
    name: "Parramatta Metro Station",
    client: "Transport for NSW",
    status: "active",
    type: "Infrastructure",
    location: "Parramatta, NSW",
    startDate: "2024-06-15",
    updatedAt: "2025-02-10",
    drawingsCount: 112,
    description: "Civil and structural drawings for underground metro station concourse.",
  },
  {
    id: "proj_003",
    name: "Royal Adelaide Hospital Expansion",
    client: "SA Health",
    status: "on-hold",
    type: "Healthcare",
    location: "Adelaide, SA",
    startDate: "2024-03-01",
    updatedAt: "2025-01-22",
    drawingsCount: 84,
    description: "New oncology wing — structural and hydraulic design review.",
  },
  {
    id: "proj_004",
    name: "Brisbane CBD Mixed-Use Tower",
    client: "Lendlease",
    status: "active",
    type: "Commercial",
    location: "Brisbane, QLD",
    startDate: "2024-11-20",
    updatedAt: "2025-02-14",
    drawingsCount: 63,
    description: "50-storey mixed-use tower — façade, structural, and services coordination.",
  },
  {
    id: "proj_005",
    name: "Yarra River Pedestrian Bridge",
    client: "City of Melbourne",
    status: "completed",
    type: "Infrastructure",
    location: "Melbourne, VIC",
    startDate: "2023-08-01",
    updatedAt: "2024-11-30",
    drawingsCount: 29,
    description: "Landmark pedestrian bridge across Yarra River — structural steel design.",
  },
  {
    id: "proj_006",
    name: "Gungahlin Town Centre Revamp",
    client: "ACT Government",
    status: "draft",
    type: "Civic",
    location: "Gungahlin, ACT",
    startDate: "2025-03-01",
    updatedAt: "2025-02-01",
    drawingsCount: 0,
    description: "Town centre redevelopment — early-stage planning and compliance review.",
  },
  {
    id: "proj_007",
    name: "Fremantle Port Expansion",
    client: "Fremantle Ports",
    status: "active",
    type: "Industrial",
    location: "Fremantle, WA",
    startDate: "2024-07-10",
    updatedAt: "2025-02-08",
    drawingsCount: 91,
    description: "Wharf extension and crane rail system — civil and geotechnical works.",
  },
  {
    id: "proj_008",
    name: "Hobart Waterfront Precinct",
    client: "City of Hobart",
    status: "completed",
    type: "Civic",
    location: "Hobart, TAS",
    startDate: "2023-04-15",
    updatedAt: "2024-09-01",
    drawingsCount: 38,
    description: "Public realm and heritage-sensitive infrastructure upgrades.",
  },
];

export const PROJECT_TYPES = [
  "Commercial",
  "Infrastructure",
  "Healthcare",
  "Industrial",
  "Residential",
  "Civic",
  "Education",
  "Other",
];

export const PROJECT_STATUSES = [
  { value: "active",                  label: "Active",       color: "bg-green-100 text-green-800"  },
  { value: "on-hold",                 label: "On Hold",      color: "bg-yellow-100 text-yellow-800"},
  { value: "ready for invoice",       label: "Ready for Invoice", color: "bg-orange-100 text-orange-800" },
  { value: "completed: invoice sent", label: "Invoice Sent", color: "bg-amber-100 text-amber-800"  },
  { value: "completed: invoice paid", label: "Invoice Paid", color: "bg-slate-100 text-slate-600"  },
  { value: "completed",               label: "Completed",    color: "bg-emerald-600 text-white"    },
  { value: "draft",                   label: "Draft",        color: "bg-blue-100 text-blue-800"    },
];

export const getStatusConfig = (status) =>
  PROJECT_STATUSES.find((s) => s.value === status) || PROJECT_STATUSES[0];

/** All values that count as "completed" (including the legacy value). */
export const COMPLETED_STATUSES = new Set(["completed", "ready for invoice", "completed: invoice sent", "completed: invoice paid"]);
export const isCompleted = (status) => COMPLETED_STATUSES.has(status);

// The fully-locked terminal status — once set, nothing about the project can
// be edited except reopening it by changing the status again.
export const LOCKED_STATUS = "completed";
export const isProjectLocked = (status) => status === LOCKED_STATUS;

// Once a project enters the Xero invoicing pipeline (or is fully Completed)
// it may already have a real invoice (and payment) in Xero, so it can no
// longer be deleted.
export const XERO_INVOICING_LOCKED_STATUSES = new Set(["ready for invoice", "completed: invoice sent", "completed: invoice paid", LOCKED_STATUS]);
